import {
  Constants,
  DxTheme,
  EmptyValue,
  formatNumericValue,
  GenericDateField,
  GenericLabeledDateField,
  Metadata,
  PriceTextField,
  RegulatorExtraDetailsType,
  SimpleLabeledField,
  Ubl,
} from '@dx-ui/dx-common';
import { Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import { cloneDeep, get, isEmpty, set } from 'lodash';
import PropTypes from 'prop-types';
import { useDataProvider } from 'ra-core';
import React, { ComponentType, useEffect, useMemo, useState } from 'react';
import {
  ComponentPropType,
  Datagrid,
  FunctionField,
  ListContextProvider,
  Loading,
  ReduxState,
  TextField,
  useLocale,
  useResourceContext,
  useTranslate,
} from 'react-admin';
import { useSelector } from 'react-redux';
import {
  AlfrescoDocumentService,
  DataHelpers,
  DocumentServiceFactory,
  InvoiceService,
  InvoiceServiceCreator,
  LineProcessor,
  PeppolService,
} from '../../services';
import { FieldTypes } from '../../shared';
import { P2pData, P2pPreviewData, P2pUblContent } from '../../shared/types';
import TaxSummaryPanel from '../../shared/webForms/fields/TaxSummaryPanel';
import TaxTotals from '../../shared/webForms/fields/TaxTotals';
import { CommaSeparator } from '../../shared/webForms/utils';
import { P2pUtils } from '../common/P2pUtils';
import {
  AddressField,
  getAccountingCustomerAddress,
  getAccountingSupplierAddress,
  getDeliveryLocationAddress,
} from '../common/PreviewAddress';
import { useCellStyle, useCommonPreviewStyles } from '../common/PreviewStyles';
import { getTaxCategoryDisplay } from './InvoicePreviewUtils';

const useStyles = makeStyles(
  (theme: DxTheme) => ({
    exchangeRate: {
      marginRight: 4,
    },
    bold: {
      fontWeight: 'bold',
    },
    rowEven: {
      backgroundColor: theme.palette.grey[100],
    },
    documentId: {
      marginLeft: '1em',
      '& p': {
        fontSize: '2em',
      },
    },
    headerRow: {
      '&.stickyCol': {
        backgroundColor: theme.app.tableBkgColor,
      },
    },
    headerCell_withStickyCols: {
      '&.stickyCol': {
        background: theme.app.tableBkgColor,
        position: 'sticky',
        left: 0,
        top: 0,
        zIndex: 20,
      },
      '&.stickyCol:first-child': {
        zIndex: 30,
      },
    },
    row_withStickyCols: {
      '& td:first-child.stickyCol': {
        position: 'sticky',
        left: 0,
        zIndex: 20,
        backgroundColor: theme.app.tableBkgColor,
      },
    },
    rowEven_withStickyCols: {
      '& td:first-child.stickyCol': {
        position: 'sticky',
        left: 0,
        zIndex: 20,
        backgroundColor: theme.palette.grey[100],
      },
    },
    headerRow_withStickyCols: {
      '& th:first-child.stickyCol': {
        position: 'sticky',
        top: 0,
        left: 0,
        zIndex: 30,
        backgroundColor: theme.app.tableBkgColor,
      },
    },
    tableScroll: (props: { sidebarOpen }) => {
      return {
        // wrapper div css class
        transition: theme.transitions.create(['width'], {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        }),
        position: 'relative',
        width: props.sidebarOpen
          ? 'calc(100vw - 320px)'
          : 'calc(100vw - 135px)',
        zIndex: 1,
        overflow: 'auto',
        height: 'auto',
        '& table': {
          borderCollapse: 'separate',
        },
        borderBottomStyle: 'ridge',
      };
    },
  }),
  { name: 'InvoicePreview' }
);

interface PropsModel {
  record: P2pData;
  basePath?: string;
  toolbar?: ComponentType<any>;
  resource?: string;
}

const InvoicePreview = (props: PropsModel) => {
  const { basePath, record, toolbar } = props;

  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const sidebarOpen = useSelector<ReduxState, boolean>(
    (state) => state.admin.ui.sidebarOpen
  );
  const classes = useStyles({ ...props, sidebarOpen });
  const commonClasses = useCommonPreviewStyles();
  const cellStyleClasses = useCellStyle();
  const resource = useResourceContext(props);
  const locale = useLocale();

  const [data, setData] = useState<P2pPreviewData | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const invoiceService = useMemo(
    () =>
      DocumentServiceFactory.create(
        InvoiceServiceCreator,
        dataProvider
      ) as InvoiceService,
    [dataProvider]
  );

  useEffect(() => {
    const getData = async () => {
      const nodeId: string | undefined = record.id;

      try {
        let properties: any = {};
        let ublProperties: any = {};
        let lines: any[];

        properties = cloneDeep(record?.properties ?? {});

        if (record?.ublProperties && record?.lines) {
          // Updated draft not yet saved
          ublProperties = cloneDeep(record?.ublProperties);
          lines = cloneDeep(record?.lines);
          // Document allowance and charge pre-formatting
          const documentLevelCharge = get(record, FieldTypes.DOC_LEVEL_CHARGES);
          const documentLevelDiscount = get(
            record,
            FieldTypes.DOC_LEVEL_DISCOUNTS
          );
          if (documentLevelCharge?.length > 0) {
            if (ublProperties.allowanceCharge === undefined) {
              set(ublProperties, 'allowanceCharge', []);
            }
            documentLevelCharge.forEach((c) => {
              ublProperties.allowanceCharge.push(c);
            });
          }
          if (documentLevelDiscount?.length > 0) {
            if (ublProperties.allowanceCharge === undefined) {
              set(ublProperties, 'allowanceCharge', []);
            }
            documentLevelDiscount.forEach((c) => {
              ublProperties.allowanceCharge.push(c);
            });
          }
        } else if (nodeId) {
          let results: P2pUblContent;
          if (AlfrescoDocumentService.isWebDocument(record)) {
            // Draft saved preview
            results = await invoiceService.loadDraft(nodeId);
            // Document allowance and charge pre-formatting
            const documentLevelCharge = get(
              results,
              FieldTypes.DOC_LEVEL_CHARGES
            );
            const documentLevelDiscount = get(
              results,
              FieldTypes.DOC_LEVEL_DISCOUNTS
            );
            if (documentLevelCharge?.length > 0) {
              if (results.ublProperties.allowanceCharge === undefined) {
                set(results, 'ublProperties.allowanceCharge', []);
              }
              documentLevelCharge.forEach((c) => {
                results.ublProperties.allowanceCharge.push(c);
              });
            }
            if (documentLevelDiscount?.length > 0) {
              if (results.ublProperties.allowanceCharge === undefined) {
                set(results, 'ublProperties.allowanceCharge', []);
              }
              documentLevelDiscount.forEach((c) => {
                results.ublProperties.allowanceCharge.push(c);
              });
            }
          } else {
            // Processed invoice preview
            results = await invoiceService.load(nodeId);
          }

          ublProperties = results?.ublProperties;
          lines = results?.lines;
        } else {
          throw new Error('record id cannot be null!');
        }
        const currencyID = DataHelpers.getCurrencyID({ properties });
        const peppolInvoice: boolean =
          DataHelpers.regulatorExtraDetails({ properties: properties }) ===
            RegulatorExtraDetailsType.PEPPOL ||
          DataHelpers.regulatorExtraDetails({ properties: properties }) ===
            RegulatorExtraDetailsType.PEPPOL_INBOUND;
        const linesView = peppolInvoice
          ? P2pUtils.convertPeppolInvoiceLines2ViewObject(lines, currencyID)
          : P2pUtils.convertInvoiceLines2ViewObject(lines, currencyID);

        const previewData: P2pPreviewData = {
          id: nodeId,
          properties,
          ublProperties,
          linesView,
        };

        setData(previewData);
        setLoading(false);
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error(error);
        setError(error);
        setLoading(false);
      }
    };
    if (!loading && !data && !error) {
      setLoading(true);
      getData();
    }
  }, [data, dataProvider, error, invoiceService, loading, record]);

  if (error) {
    return (
      <Typography variant='h5'>
        {translate('dxMessages.error_messages.unable_to_display')}
      </Typography>
    );
  } else if (loading || !data) {
    return <Loading />;
  } else {
    const peppolInvoice: boolean =
      DataHelpers.regulatorExtraDetails(data) ===
        RegulatorExtraDetailsType.PEPPOL ||
      DataHelpers.regulatorExtraDetails(data) ===
        RegulatorExtraDetailsType.PEPPOL_INBOUND;
    const taxSummaryTable = get(data.ublProperties, Ubl.taxSubtotal) || [];
    const currencyID = DataHelpers.getCurrencyID(data);
    const lineIds = Object.keys(data?.linesView ?? []);
    const documentLevelAllowanceCharge = data?.ublProperties.allowanceCharge;
    const exchangeRate = get(data, Ubl.paymentExchangeRateCalculationRate);
    const excisePayerCode = get(data, Ubl.AccountingSupplierTaxSchemeCompanyID);
    const projectReferenceID = get(data, Ubl.projectReferenceID);
    const invoiceDraft = AlfrescoDocumentService.isWebDocument(data);

    const accessPoint = `${get(
      data,
      'ublProperties.accountingCustomerParty.party.endpointID.schemeID'
    )}:${get(
      data,
      'ublProperties.accountingCustomerParty.party.endpointID.value'
    )}`;
    const documentDiscount: any[] = [];
    const documentCharge: any[] = [];
    let documentDiscountLineIds: string[] = [];
    let documentChargeLineIds: string[] = [];
    if (documentLevelAllowanceCharge) {
      documentLevelAllowanceCharge.forEach((e) => {
        if (
          e.chargeIndicator?.value === true ||
          e.charge_reason_code?.value !== undefined
        ) {
          documentCharge.push(e);
        } else {
          documentDiscount.push(e);
        }
      });
      documentDiscountLineIds = Object.keys(documentDiscount ?? []);
      documentChargeLineIds = Object.keys(documentCharge ?? []);
    }
    const taxRepresentativeParty = get(
      data.ublProperties,
      'taxRepresentativeParty'
    );

    // --------- Invoice flow identification -------------------
    // In the PEPPOL Outbound context, the customizationID and the profileID are saved in
    // the additionalDocumentReference identified by DOCPROCESS :
    /*
      example:
        "additionalDocumentReference" : [ {
          "id" : {
            "value" : "DOCPROCESS"
          },
          "xpath" : [ {
            "value" : "CustomizationID:urn:cen.eu:en16931:2017#compliant#urn:fdc:peppol.eu:2017:poacc:billing:3.0"
          }, {
            "value" : "ProfileID:urn:fdc:peppol.eu:2017:poacc:billing:01:1.0"
        } ]
    */
    // Therefore independently of the flow type if these entries exist,
    // we have to use them else we use the standard ones
    let customizationID = get(data, Ubl.customizationID);
    let profileID = get(data, Ubl.profileID);
    const docProcessRef = (
      data?.ublProperties.additionalDocumentReference ?? []
    ).find((a: any) => a.id?.value === 'DOCPROCESS');
    if (docProcessRef) {
      const xpathValues: string[] = docProcessRef?.xpath
        ?.map((x: any) => x.value)
        .filter((v: string) => v !== undefined);
      const custId = xpathValues.find((v: string) =>
        v.startsWith('CustomizationID:')
      );
      if (custId) {
        customizationID = custId.replace('CustomizationID:', '');
      }
      const profId = xpathValues.find((v: string) =>
        v.startsWith('ProfileID:')
      );
      if (profId) {
        profileID = profId.replace('ProfileID:', '');
      }
    }

    const UBL_BE_Invoice: boolean =
      customizationID === PeppolService.PEPPOL_CUSTOMIZATION_ID_BE;

    const additionalDocumentReferences: any[] =
      data?.ublProperties.additionalDocumentReference?.filter(
        (a: any) =>
          // Removes system references (docprocess and Belgium specificities)
          a.id?.value !== 'DOCPROCESS' &&
          a.id?.value !== 'UBL.BE' &&
          a.documentDescription?.value !== 'CommercialInvoice' &&
          a.documentDescription?.value !== 'CreditNote' &&
          a.documentDescription?.[0]?.value !== 'CommercialInvoice' &&
          a.documentDescription?.[0]?.value !== 'CreditNote'
      );
    const additionalDocumentReferenceLineIds = Object.keys(
      additionalDocumentReferences ?? []
    );

    const payeePartyName =
      get(data, 'ublProperties.payeeParty.partyName.name.value') ||
      get(data, 'ublProperties.payeeParty.partyName[0].name.value');

    let hasBaseQuantity: boolean = false;
    hasBaseQuantity = lineIds.some(
      (lineId, index) =>
        get(data?.linesView[lineIds[index]], Ubl.baseQuantity) !== undefined &&
        get(data?.linesView[lineIds[index]], Ubl.baseQuantity) !== 1
    );

    let hasExemptionReasonCode: boolean = false;
    hasExemptionReasonCode = lineIds.some(
      (lineId, index) =>
        get(data?.linesView[lineIds[index]], Ubl.taxExemptionReasonCode) !==
          undefined && !LineProcessor.isLineSGR(data?.linesView[lineIds[index]])
    );
    let hasNonStandardTaxCategory: boolean = false;
    hasNonStandardTaxCategory = lineIds.some(
      (lineId, index) =>
        get(
          data?.linesView[lineIds[index]],
          'item.classifiedTaxCategory[0].id.value'
        ) !== undefined &&
        get(
          data?.linesView[lineIds[index]],
          'item.classifiedTaxCategory[0].id.value'
        ) !== Constants.TAX_SCHEME_ID_S &&
        !LineProcessor.isLineSGR(data?.linesView[lineIds[index]])
    );

    let hasAdditionalItemProperties: boolean = false;
    hasAdditionalItemProperties = lineIds.some(
      (lineId, index) =>
        get(
          data?.linesView[lineIds[index]],
          'item.additionalItemProperty[0].name.value'
        ) !== undefined
    );

    let hasLineOrderId: boolean = false;
    hasLineOrderId = lineIds.some(
      (lineId, index) =>
        get(data?.linesView[lineIds[index]], Ubl.lineId) !== undefined ||
        get(
          data?.linesView[lineIds[index]],
          'orderLineReference[0].lineID.value'
        ) !== undefined
    );

    let hasLineDocumentRef: boolean = false;
    hasLineDocumentRef = lineIds.some(
      (lineId, index) =>
        get(data?.linesView[lineIds[index]], 'documentReference.id.value') !==
          undefined ||
        get(
          data?.linesView[lineIds[index]],
          'documentReference[0].id.value'
        ) !== undefined
    );

    let hasStartOrEndLinesPeriodDates: boolean = false;
    hasStartOrEndLinesPeriodDates = lineIds.some((lineId, index) => {
      return (
        get(
          data?.linesView[lineIds[index]],
          'invoicePeriod.startDate.value'
        ) !== undefined ||
        get(data?.linesView[lineIds[index]], 'invoicePeriod.endDate.value') !==
          undefined ||
        get(
          data?.linesView[lineIds[index]],
          'invoicePeriod[0].startDate.value'
        ) !== undefined ||
        get(
          data?.linesView[lineIds[index]],
          'invoicePeriod[0].endDate.value'
        ) !== undefined
      );
    });

    let hasLineNote: boolean = false;
    hasLineNote = lineIds.some(
      (lineId, index) =>
        get(data?.linesView[lineIds[index]], Ubl.lineNote) !== undefined
    );
    let hasLineItemClassification: boolean = false;
    hasLineItemClassification = lineIds.some(
      (lineId, index) =>
        get(data?.linesView[lineIds[index]], Ubl.itemClassificationCode) !==
          undefined ||
        get(
          data?.linesView[lineIds[index]],
          'item.commodityClassification[0].itemClassificationCode.value'
        ) !== undefined
    );
    return (
      <>
        <Paper square classes={{ root: commonClasses.paper }}>
          <Grid item container justify='center' alignItems='baseline' xs={8}>
            <Grid item>
              <Typography variant='h6' style={{ fontSize: '2em' }}>
                {translate('dxMessages.invoices.document_label')}
              </Typography>
            </Grid>
            <Grid item className={Metadata.documentId}>
              <SimpleLabeledField
                label=''
                data={get(data.properties, Metadata.documentId)}
                oneLine
                className={classNames(Metadata.documentId, classes.documentId)}
              />
            </Grid>
          </Grid>
          <Grid
            item
            container
            classes={{ container: commonClasses.section }}
            xs={8}
          >
            <Grid item xs={2}>
              <GenericLabeledDateField
                label={translate('dxMessages.headers.issueDate')}
                id={Metadata.issueDate}
                record={data}
                className={Metadata.issueDate}
                bold
              />
            </Grid>
            <Grid item xs={2}>
              <GenericLabeledDateField
                label={translate('dxMessages.invoices.due_date')}
                id={!invoiceDraft ? Ubl.headerDueDate : Ubl.dueDate}
                record={data}
                className={Metadata.dueDate}
                bold
              />
            </Grid>
            <Grid item xs={2}>
              <GenericLabeledDateField
                label={translate('dxMessages.headers.deliveryDate')}
                id={Ubl.deliveryDate}
                record={data}
                className={'deliveryDate'}
                bold
              />
            </Grid>
            <Grid item xs={2}>
              <FunctionField
                source='ublProperties.invoicePeriod.startDate.value'
                label=''
                sortable={false}
                record={data.ublProperties}
                render={(record) => {
                  const value =
                    get(record, 'invoicePeriod.startDate') ||
                    get(record, 'invoicePeriod[0].startDate');
                  return (
                    <GenericLabeledDateField
                      label={translate(
                        'dxMessages.headers.invoicePeriodStartDate'
                      )}
                      id='value'
                      record={value}
                      className={'invoicePeriodStartDate'}
                      bold
                    />
                  );
                }}
              />
            </Grid>
            <Grid item xs={2}>
              <FunctionField
                source='ublProperties.invoicePeriod.endDate.value'
                label=''
                record={data.ublProperties}
                sortable={false}
                render={(record) => {
                  const value =
                    get(record, 'invoicePeriod.endDate') ||
                    get(record, 'invoicePeriod[0].endDate');
                  return (
                    <GenericLabeledDateField
                      label={translate(
                        'dxMessages.headers.invoicePeriodEndDate'
                      )}
                      id='value'
                      record={value}
                      className={'invoicePeriodEndDate'}
                      bold
                    />
                  );
                }}
              />
            </Grid>
          </Grid>
          <Grid
            container
            direction='row'
            classes={{ container: commonClasses.graySection }}
          >
            <Grid item container direction='column' xs={4}>
              <Grid item container>
                <Grid item>
                  <Typography variant='h6'>
                    {translate('dxMessages.headers.supplier')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  className={Metadata.issuerName}
                  style={{ marginLeft: '1em' }}
                >
                  <Typography variant='h5'>
                    {get(data.properties, Metadata.issuerName)}
                  </Typography>
                </Grid>
              </Grid>

              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.id')}
                  data={get(data.properties, Metadata.issuerId)}
                  className={Metadata.issuerId}
                  oneLine
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.supplierReceiverCode')}
                  data={get(data, Ubl.accountingSupplierCustomerAssignedID)}
                  oneLine
                  className={'accountingSupplierCustomerAssignedID'}
                  bold
                />
              </Grid>
              {excisePayerCode && (
                <Grid item style={{ marginLeft: '1em' }}>
                  <SimpleLabeledField
                    label={translate('dxMessages.headers.excisePayerCode')}
                    data={excisePayerCode}
                    oneLine
                    className={'excisePayerCode'}
                    bold
                  />
                </Grid>
              )}
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.invoices.iban')}
                  data={get(data, Ubl.payeeIban)}
                  className={'issuerIban'}
                  oneLine
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.invoices.bank')}
                  data={get(data, Ubl.payeeBank)}
                  className={'issuerBank'}
                  oneLine
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <AddressField
                  address={getAccountingSupplierAddress(data)}
                  className={'accountingSupplierAddress'}
                  bold
                />
              </Grid>
            </Grid>
            <Grid item container direction='column' xs={4}>
              <Grid item container>
                <Grid item>
                  <Typography variant='h6'>
                    {translate('dxMessages.headers.customer')}
                  </Typography>
                </Grid>
                <Grid
                  item
                  className={Metadata.recipientName}
                  style={{ marginLeft: '1em' }}
                >
                  <Typography variant='h5'>
                    {get(data.properties, Metadata.recipientName)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.id')}
                  data={get(data.properties, Metadata.recipientId)}
                  className={Metadata.recipientId}
                  oneLine
                  bold
                />
              </Grid>

              {peppolInvoice && (
                <Grid item style={{ marginLeft: '1em' }}>
                  <SimpleLabeledField
                    label={translate('dxMessages.peppol.accessPoint.title')}
                    data={accessPoint}
                    className={'peppolEndpoint'}
                    oneLine
                    bold
                  />
                </Grid>
              )}

              {!peppolInvoice && (
                <Grid item style={{ marginLeft: '1em' }}>
                  <SimpleLabeledField
                    label={translate('dxMessages.invoices.iban')}
                    data={get(data, Ubl.payerIban)}
                    className={'recipientIban'}
                    oneLine
                    bold
                  />
                </Grid>
              )}
              {!peppolInvoice && (
                <Grid item style={{ marginLeft: '1em' }}>
                  <SimpleLabeledField
                    label={translate('dxMessages.invoices.bank')}
                    data={get(data, Ubl.payerBank)}
                    className={'recipientBank'}
                    oneLine
                    bold
                  />
                </Grid>
              )}
              <Grid item style={{ marginLeft: '1em' }}>
                <AddressField
                  address={getAccountingCustomerAddress(data)}
                  className={'accountingCustomerAddress'}
                  bold
                />
              </Grid>
            </Grid>
            <Grid item container direction='column' xs={4}></Grid>
            <Grid item container direction='column' xs={4}>
              <Grid item>
                <Typography variant='h6' style={{ marginTop: '1em' }}>
                  {translate('dxMessages.headers.contact')}
                </Typography>
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.contactName')}
                  data={get(data, Ubl.accountingSupplierContactName)}
                  className={'supplierContactName'}
                  oneLine
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.contactPhone')}
                  data={get(data, Ubl.accountingSupplierContactTelephone)}
                  className={'supplierContactPhone'}
                  oneLine
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.contactElectronicMail')}
                  data={get(data, Ubl.accountingSupplierContactElectronicMail)}
                  className={'supplierContactElectronicMail'}
                  oneLine
                  bold
                />
              </Grid>
            </Grid>
            <Grid item container direction='column' xs={4}>
              <Grid item>
                <Typography variant='h6' style={{ marginTop: '1em' }}>
                  {translate('dxMessages.headers.contact')}
                </Typography>
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.contactName')}
                  data={get(
                    data,
                    'ublProperties.accountingCustomerParty.party.contact.name.value'
                  )}
                  className={'customerContactName'}
                  oneLine
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.contactPhone')}
                  data={get(
                    data,
                    'ublProperties.accountingCustomerParty.party.contact.telephone.value'
                  )}
                  className={'customerContactPhone'}
                  oneLine
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.contactElectronicMail')}
                  data={
                    get(
                      data,
                      'ublProperties.accountingCustomerParty.party.contact.electronicEmail.value'
                    ) ||
                    get(
                      data,
                      'ublProperties.accountingCustomerParty.party.contact.electronicMail.value'
                    )
                  }
                  className={'customerContactElectronicMail'}
                  oneLine
                  bold
                />
              </Grid>
            </Grid>
            <Grid item container direction='column' xs={4}></Grid>
          </Grid>
          <Grid container classes={{ container: commonClasses.section }}>
            <Grid item container xs={4}>
              <Grid item>
                <Typography variant='h6'>
                  {translate('dxMessages.headers.documentReferences')}
                </Typography>
              </Grid>
              <Grid
                item
                container
                direction='row'
                style={{ alignItems: 'baseline ', marginLeft: '1em' }}
              >
                <Grid item>
                  <SimpleLabeledField
                    label={translate('dxMessages.invoices.initialInvoice')}
                    data={get(data, Ubl.invoiceDocumentReferenceId)}
                    oneLine
                    className={'referenceInvoiceId'}
                    bold
                  />
                </Grid>
                <CommaSeparator />
                <Grid item>
                  <GenericLabeledDateField
                    label={translate('dxMessages.headers.issueDate')}
                    id={Ubl.invoiceDocumentReferenceIssueDate}
                    record={data}
                    oneLine
                    className={'referenceInvoiceIssueDate'}
                    bold
                  />
                </Grid>
              </Grid>
              <Grid
                item
                container
                direction='row'
                style={{ alignItems: 'baseline ', marginLeft: '1em' }}
              >
                <Grid item>
                  <SimpleLabeledField
                    label={translate('dxMessages.headers.orderId')}
                    data={get(data.properties, Metadata.orderId)}
                    oneLine
                    className={Metadata.orderId}
                    bold
                  />
                </Grid>
                <CommaSeparator />
                <Grid item>
                  <GenericLabeledDateField
                    label={translate('dxMessages.headers.orderDate')}
                    id={Metadata.orderDate}
                    record={data}
                    oneLine
                    className={Metadata.orderDate}
                    bold
                  />
                </Grid>
              </Grid>

              <Grid
                item
                container
                direction='row'
                style={{ alignItems: 'baseline ', marginLeft: '1em' }}
              >
                <Grid item>
                  <SimpleLabeledField
                    label={translate('dxMessages.headers.despatchAdviceId')}
                    data={get(data.properties, Metadata.despatchAdviceId)}
                    oneLine
                    className={Metadata.despatchAdviceId}
                    bold
                  />
                </Grid>
                <CommaSeparator />
                <Grid item>
                  <GenericLabeledDateField
                    label={translate('dxMessages.headers.despatchAdviceDate')}
                    id={Metadata.despatchAdviceDate}
                    record={data}
                    oneLine
                    className={Metadata.despatchAdviceDate}
                    bold
                  />
                </Grid>
              </Grid>
              <Grid
                item
                container
                direction='row'
                style={{ alignItems: 'baseline ', marginLeft: '1em' }}
              >
                <Grid item>
                  <SimpleLabeledField
                    label={translate('dxMessages.headers.receiptAdviceId')}
                    data={get(data.properties, Metadata.receiptAdviceId)}
                    oneLine
                    className={Metadata.receiptAdviceId}
                    bold
                  />
                </Grid>
                <CommaSeparator />
                <Grid item>
                  <GenericLabeledDateField
                    label={translate('dxMessages.headers.receiptAdviceDate')}
                    id={Metadata.receiptAdviceDate}
                    record={data}
                    oneLine
                    className={Metadata.receiptAdviceDate}
                    bold
                  />
                </Grid>
              </Grid>
              <Grid
                item
                container
                direction='row'
                style={{ alignItems: 'baseline ', marginLeft: '1em' }}
              >
                <Grid item>
                  <SimpleLabeledField
                    label={translate('dxMessages.headers.contractId')}
                    data={get(data.properties, Metadata.contractId)}
                    oneLine
                    className={Metadata.contractId}
                    bold
                  />
                </Grid>
                <CommaSeparator />
                <Grid item>
                  <GenericLabeledDateField
                    label={translate('dxMessages.headers.contractDate')}
                    id={Metadata.contractDate}
                    record={data}
                    oneLine
                    className={Metadata.contractDate}
                    bold
                  />
                </Grid>
              </Grid>

              {peppolInvoice && (
                <Grid item container style={{ marginLeft: '1em' }}>
                  <SimpleLabeledField
                    label={translate('dxMessages.headers.projectReferenceID')}
                    data={projectReferenceID}
                    oneLine
                    className={'projectReferenceID'}
                    bold
                  />
                </Grid>
              )}

              {/* Additional document references */}
              {additionalDocumentReferences?.length > 0 && (
                <Grid item style={{ marginLeft: '1em' }}>
                  <Grid item>
                    <Typography
                      variant='caption'
                      classes={{ root: classes.bold }}
                    >
                      {translate(
                        'dxMessages.headers.additionalDocumentReference'
                      )}
                      {' : '}
                    </Typography>
                  </Grid>
                  <Grid item container direction='column'>
                    <ListContextProvider
                      value={{
                        ids: additionalDocumentReferenceLineIds,
                        data: { ...additionalDocumentReferences },
                        currentSort: {},
                        resource,
                        selectedIds: [],
                      }}
                    >
                      <Datagrid
                        classes={{
                          row: commonClasses.row,
                          rowCell: commonClasses.linesCell,
                          headerCell: classNames(
                            commonClasses.headerCell,
                            classes.bold
                          ),
                          tbody: commonClasses.tbody,
                          table: 'additionalDocumentReferences',
                        }}
                      >
                        <TextField
                          source='id.value'
                          label='dxMessages.headers.id'
                          sortable={false}
                        />
                        <FunctionField
                          source='documentDescription.value'
                          label='dxMessages.headers.description'
                          sortable={false}
                          render={(record) => {
                            let value =
                              get(record, 'documentDescription.value') ||
                              get(record, 'documentDescription[0].value');
                            return (
                              <Typography>
                                {!isEmpty(value) ? value : EmptyValue}
                              </Typography>
                            );
                          }}
                        />
                      </Datagrid>
                    </ListContextProvider>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item container direction='column' xs={4}>
              <Grid item>
                <Typography variant='h6'>
                  {translate('dxMessages.invoices.delivery')}
                </Typography>
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.name')}
                  data={
                    get(data, Ubl.deliveryLocationName)
                      ? get(data, Ubl.deliveryLocationName)
                      : get(data.properties, Metadata.locationName)
                  }
                  oneLine
                  className={Metadata.locationName}
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <SimpleLabeledField
                  label={translate('dxMessages.headers.gln')}
                  data={get(data, Ubl.deliveryLocationGLN)}
                  oneLine
                  className={'deliveryLocationGLN'}
                  bold
                />
              </Grid>
              <Grid item style={{ marginLeft: '1em' }}>
                <AddressField
                  label={translate('dxMessages.headers.address')}
                  address={getDeliveryLocationAddress(data)}
                  className={'deliveryLocationAddress'}
                  bold
                />
              </Grid>
            </Grid>
            <Grid item container direction='column' xs={6}></Grid>
          </Grid>
          {(taxRepresentativeParty || payeePartyName) && (
            <Grid
              container
              direction='row'
              classes={{ container: commonClasses.graySection }}
            >
              <Grid item container direction='column' xs={4}>
                {taxRepresentativeParty && (
                  <>
                    <Grid item>
                      <Typography variant='h6'>
                        {translate('dxMessages.invoices.taxRepresentative')}
                      </Typography>
                    </Grid>
                    <Grid item style={{ marginLeft: '1em' }}>
                      <SimpleLabeledField
                        label={translate('dxMessages.headers.name')}
                        data={
                          get(
                            taxRepresentativeParty,
                            'partyName[0].name.value'
                          ) ||
                          get(taxRepresentativeParty, 'partyName.name.value')
                        }
                        className={'TaxRepresentativeName'}
                        oneLine
                        bold
                      />
                    </Grid>
                    <Grid item style={{ marginLeft: '1em' }}>
                      <SimpleLabeledField
                        label={translate('dxMessages.headers.id')}
                        data={
                          get(
                            taxRepresentativeParty,
                            'partyTaxScheme.companyID.value'
                          ) ||
                          get(
                            taxRepresentativeParty,
                            'partyTaxScheme[0].companyID.value'
                          )
                        }
                        className={'taxRepresentativeCompanyID'}
                        oneLine
                        bold
                      />
                    </Grid>
                    <Grid item style={{ marginLeft: '1em' }}>
                      <AddressField
                        address={get(taxRepresentativeParty, 'postalAddress')}
                        className={'taxRepresentativeAddress'}
                        bold
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid item container direction='column' xs={4}>
                {payeePartyName && (
                  <>
                    <Grid item>
                      <Typography variant='h6'>
                        {translate('dxMessages.headers.payeePartyDetails')}
                      </Typography>
                    </Grid>
                    <Grid item style={{ marginLeft: '1em' }}>
                      <SimpleLabeledField
                        label={translate('dxMessages.headers.name')}
                        data={payeePartyName}
                        className={'payeePartyName'}
                        oneLine
                        bold
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid item container direction='column' xs={4}></Grid>
            </Grid>
          )}
          {documentDiscount.length > 0 && (
            <Grid
              item
              container
              classes={{ container: commonClasses.section }}
              direction='column'
              xs={8}
            >
              <Grid item>
                <Typography variant='h6'>
                  {translate('dxMessages.invoices.documentLevelDiscounts')}
                </Typography>
              </Grid>
              <Grid item>
                <ListContextProvider
                  value={{
                    ids: documentDiscountLineIds,
                    data: { ...documentDiscount },
                    currentSort: {},
                    resource,
                    selectedIds: [],
                  }}
                >
                  <Datagrid
                    classes={{
                      row: commonClasses.row,
                      rowEven: classes.rowEven,
                      rowCell: commonClasses.linesCell,
                      headerCell: classNames(
                        commonClasses.headerCell,
                        classes.bold
                      ),
                      tbody: commonClasses.tbody,
                      table: 'documentLevelDiscounts',
                    }}
                  >
                    <PriceTextField
                      source='amount.value'
                      label='dxMessages.headers.discountAmount'
                      sortable={false}
                      headerClassName='alignRight'
                      cellClassName='alignRight'
                    />

                    <FunctionField
                      source='taxCategory.percent.value'
                      label='dxMessages.headers.vatPercent'
                      sortable={false}
                      render={(record) => {
                        let value = get(record, 'taxCategory.percent.value');
                        if (value === undefined) {
                          value = get(record, 'taxCategory[0].percent.value');
                        }
                        return (
                          <Typography>
                            {!isNaN(value) && value !== null
                              ? `${value}%`
                              : EmptyValue}
                          </Typography>
                        );
                      }}
                    />
                    <FunctionField
                      source='taxCategory.id.value'
                      label='dxMessages.invoices.tax_category_code'
                      sortable={false}
                      render={(record) => {
                        // Priority is given to name over id.
                        // name can fit PEPPOL UBL.BE for instance.
                        // 1 : name in taxCategory(object) : most often Draft UBL
                        // 2 : name in taxCategory(array) : most often nonDraft UBL.
                        // 3 : id in taxCategory(object) : most often Draft UBL.
                        // 4 : id in taxCategory(array) : most often non Draft UBL.
                        const value =
                          get(record, 'taxCategory.name.value') ||
                          get(record, 'taxCategory[0].name.value') ||
                          get(record, 'taxCategory.id.value') ||
                          get(record, 'taxCategory[0].id.value');
                        let { display: displayScheme, tooltip } =
                          getTaxCategoryDisplay(value, translate);
                        return tooltip ? (
                          <>
                            <Tooltip
                              title={tooltip}
                              style={{
                                pointerEvents: 'initial',
                                cursor: 'default',
                              }}
                            >
                              <Typography>
                                {displayScheme !== null
                                  ? displayScheme
                                  : EmptyValue}
                              </Typography>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography>
                            {displayScheme !== null
                              ? displayScheme
                              : EmptyValue}
                          </Typography>
                        );
                      }}
                      style={{ display: 'block', width: '12em' }}
                    />
                    <FunctionField
                      source='discount_reason_code.value'
                      label='dxMessages.invoices.discount_reason_code'
                      sortable={false}
                      render={(record) => {
                        const value =
                          get(record, 'discount_reason_code.value') ||
                          get(record, 'allowanceChargeReasonCode.value');
                        return (
                          <Typography>
                            {value
                              ? translate(
                                  `dxMessages.invoices.discountReasonCode.${value}`,
                                  { _: value ? value : EmptyValue }
                                )
                              : EmptyValue}
                          </Typography>
                        );
                      }}
                      style={{ display: 'block', width: '12em' }}
                    />
                  </Datagrid>
                </ListContextProvider>
              </Grid>
            </Grid>
          )}
          {documentCharge.length > 0 && (
            <Grid
              item
              container
              classes={{ container: commonClasses.section }}
              direction='column'
              xs={8}
            >
              <Grid item>
                <Typography variant='h6'>
                  {translate('dxMessages.invoices.documentLevelCharges')}
                </Typography>
              </Grid>
              <Grid item>
                <ListContextProvider
                  value={{
                    ids: documentChargeLineIds,
                    data: { ...documentCharge },
                    currentSort: {},
                    resource,
                    selectedIds: [],
                  }}
                >
                  <Datagrid
                    classes={{
                      row: commonClasses.row,
                      rowEven: classes.rowEven,
                      rowCell: commonClasses.linesCell,
                      headerCell: classNames(
                        commonClasses.headerCell,
                        classes.bold
                      ),
                      tbody: commonClasses.tbody,
                      table: 'documentLevelCharges',
                    }}
                  >
                    <PriceTextField
                      source='amount.value'
                      label='dxMessages.headers.taxAmount'
                      sortable={false}
                      headerClassName='alignRight'
                      cellClassName='alignRight'
                    />

                    <FunctionField
                      source='taxCategory.percent.value'
                      label='dxMessages.headers.vatPercent'
                      sortable={false}
                      render={(record) => {
                        let value = get(record, 'taxCategory.percent.value');
                        if (value === undefined) {
                          value = get(record, 'taxCategory[0].percent.value');
                        }
                        return (
                          <Typography>
                            {!isNaN(value) && value !== null
                              ? `${value}%`
                              : EmptyValue}
                          </Typography>
                        );
                      }}
                    />
                    <FunctionField
                      source='taxCategory.id.value'
                      label='dxMessages.invoices.tax_category_code'
                      sortable={false}
                      render={(record) => {
                        // Priority is given to name over id.
                        // name can fit PEPPOL UBL.BE for instance.
                        // 1 : name in taxCategory(object) : most often Draft UBL
                        // 2 : name in taxCategory(array) : most often nonDraft UBL.
                        // 3 : id in taxCategory(object) : most often Draft UBL.
                        // 4 : id in taxCategory(array) : most often non Draft UBL.
                        const value =
                          get(record, 'taxCategory.name.value') ||
                          get(record, 'taxCategory[0].name.value') ||
                          get(record, 'taxCategory.id.value') ||
                          get(record, 'taxCategory[0].id.value');
                        let { display: displayScheme, tooltip } =
                          getTaxCategoryDisplay(value, translate);
                        return tooltip ? (
                          <>
                            <Tooltip
                              title={tooltip}
                              style={{
                                pointerEvents: 'initial',
                                cursor: 'default',
                              }}
                            >
                              <Typography>
                                {displayScheme !== null
                                  ? displayScheme
                                  : EmptyValue}
                              </Typography>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography>
                            {displayScheme !== null
                              ? displayScheme
                              : EmptyValue}
                          </Typography>
                        );
                      }}
                      style={{ display: 'block', width: '12em' }}
                    />
                    <FunctionField
                      source='charge_reason_code.value'
                      label='dxMessages.invoices.charge_reason_code'
                      sortable={false}
                      render={(record) => {
                        const value =
                          get(record, 'charge_reason_code.value') ||
                          get(record, 'allowanceChargeReasonCode.value');
                        return (
                          <Typography>
                            {value
                              ? translate(
                                  `dxMessages.invoices.chargeReasonCode.${value}`,
                                  { _: value ? value : EmptyValue }
                                )
                              : EmptyValue}
                          </Typography>
                        );
                      }}
                      style={{ display: 'block', width: '12em' }}
                    />
                  </Datagrid>
                </ListContextProvider>
              </Grid>
            </Grid>
          )}
          <Grid
            container
            direction='row'
            classes={{ container: commonClasses.section }}
          >
            <Grid
              item
              container
              direction='column'
              classes={{ container: commonClasses.section }}
            >
              <Grid container item>
                <Grid item container xs={2} justify='flex-start'>
                  <SimpleLabeledField
                    label={translate('dxMessages.headers.currency')}
                    data={currencyID}
                    oneLine
                    className={Metadata.currency}
                  />
                </Grid>
                {exchangeRate !== undefined && (
                  <Grid item container xs={10} justify='flex-end'>
                    <Grid item>
                      <SimpleLabeledField
                        label={translate(
                          'dxMessages.headers.paymentExchangeRateCalculationRate'
                        )}
                        data={exchangeRate}
                        oneLine
                        className={`paymentExchangeRateCalculationRate ${classes.exchangeRate}`}
                      />
                    </Grid>
                    <Grid item>
                      <SimpleLabeledField
                        label={translate(
                          'dxMessages.headers.paymentExchangeRateSourceCurrencyCode'
                        )}
                        data={get(
                          data,
                          Ubl.paymentExchangeRateSourceCurrencyCode
                        )}
                        oneLine
                        className={`paymentExchangeRateSourceCurrencyCode ${classes.exchangeRate}`}
                      />
                    </Grid>
                    <Grid item>
                      <SimpleLabeledField
                        label={translate(
                          'dxMessages.headers.paymentExchangeRateTargetCurrencyCode'
                        )}
                        data={get(
                          data,
                          Ubl.paymentExchangeRateTargetCurrencyCode
                        )}
                        oneLine
                        className={'paymentExchangeRateTargetCurrencyCode'}
                      />
                    </Grid>
                  </Grid>
                )}
              </Grid>
              <Grid item>
                <ListContextProvider
                  value={{
                    ids: lineIds,
                    data: data.linesView,
                    currentSort: { field: 'nr', order: 'ASC' },
                    resource,
                    selectedIds: [],
                  }}
                >
                  <div className={classes.tableScroll}>
                    <Datagrid
                      classes={{
                        headerRow: classNames(
                          classes.headerRow,
                          classes.headerRow_withStickyCols
                        ),
                        row: classes.row_withStickyCols,
                        rowEven: classNames(
                          classes.rowEven,
                          classes.rowEven_withStickyCols
                        ),
                        rowCell: commonClasses.linesCell,
                        headerCell: classNames(
                          classes.bold,
                          classes.headerCell_withStickyCols
                        ),
                        tbody: commonClasses.tbody,
                        table: 'lines',
                      }}
                    >
                      <TextField
                        source='nr'
                        label='dxMessages.headers.number'
                        sortable={false}
                        headerClassName='stickyCol'
                        cellClassName='stickyCol'
                      />
                      {hasLineOrderId && (
                        <FunctionField
                          source={Ubl.lineId}
                          label='dxMessages.invoices.lineId'
                          sortable={false}
                          render={(record) => {
                            const value =
                              get(record, Ubl.lineId) ||
                              get(record, 'orderLineReference[0].lineID.value');
                            return (
                              <Typography>
                                {value ? value : EmptyValue}
                              </Typography>
                            );
                          }}
                        />
                      )}
                      {hasLineDocumentRef && (
                        <FunctionField
                          source={'documentReference.id.value'}
                          label='dxMessages.headers.documentReferenceId'
                          sortable={false}
                          render={(record) => {
                            const value =
                              get(record, 'documentReference.id.value') ||
                              get(record, 'documentReference[0].id.value');
                            return (
                              <Typography>
                                {value ? value : EmptyValue}
                              </Typography>
                            );
                          }}
                        />
                      )}
                      {hasLineNote && (
                        <TextField
                          source={Ubl.lineNote}
                          label='dxMessages.invoices.note'
                          sortable={false}
                          style={{ display: 'inline-flex', minWidth: '10em' }}
                        />
                      )}
                      <TextField
                        source={Ubl.codeClient}
                        label='dxMessages.headers.codeClient'
                        sortable={false}
                        style={{ display: 'inline-flex', minWidth: '8em' }}
                      />
                      <TextField
                        source={Ubl.codeSupplier}
                        label='dxMessages.headers.codeSupplier'
                        sortable={false}
                        style={{ display: 'inline-flex', minWidth: '8em' }}
                      />
                      <TextField
                        source={Ubl.codeStandard}
                        label='dxMessages.headers.codeStandard'
                        sortable={false}
                        style={{ display: 'inline-flex', minWidth: '8em' }}
                      />
                      <TextField
                        source={Ubl.description}
                        label='dxMessages.headers.description'
                        sortable={false}
                        classes={{ root: cellStyleClasses.cellWrapped }}
                        style={{ display: 'block', width: '12em' }}
                      />
                      {hasAdditionalItemProperties && (
                        <FunctionField
                          source={'item.additionalItemProperty'}
                          label='dxMessages.headers.additionalItemProperties'
                          sortable={false}
                          render={(record) => {
                            const additionalItemProperties = get(
                              record,
                              'item.additionalItemProperty'
                            );
                            return (
                              <table>
                                <tbody>
                                  {additionalItemProperties?.map((s) => {
                                    const value =
                                      s.value?.value ||
                                      s.listValue?.value ||
                                      s.valueQuantity?.value;
                                    return (
                                      <tr key={s.name.value}>
                                        <td>
                                          <Typography
                                            variant='caption'
                                            style={{
                                              fontWeight: 'bold',
                                              fontSize: '0.6rem',
                                            }}
                                          >
                                            {`${s.name.value} :`}
                                          </Typography>
                                        </td>
                                        <td>
                                          <Typography
                                            variant='caption'
                                            style={{
                                              fontSize: '0.6rem',
                                            }}
                                          >
                                            {value}
                                          </Typography>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            );
                          }}
                          style={{ display: 'block', minWidth: '14em' }}
                        />
                      )}
                      <TextField
                        source={Ubl.quantityInvoice}
                        label='dxMessages.headers.quantity'
                        sortable={false}
                      />
                      {hasBaseQuantity && (
                        <FunctionField
                          source={Ubl.baseQuantity}
                          label='dxMessages.headers.baseQuantity'
                          sortable={false}
                          render={(record) => {
                            const value = get(record, Ubl.baseQuantity);
                            return (
                              <Typography>
                                {!isNaN(value) && value !== null
                                  ? `${value}`
                                  : EmptyValue}
                              </Typography>
                            );
                          }}
                          style={{ minWidth: '3em' }}
                        />
                      )}
                      <TextField
                        source={Ubl.unitCodeInvoice}
                        label='dxMessages.headers.um'
                        sortable={false}
                      />
                      {!peppolInvoice && (
                        <PriceTextField
                          source={Ubl.price}
                          label='dxMessages.headers.price'
                          sortable={false}
                          headerClassName='alignRight'
                          cellClassName='alignRight'
                        />
                      )}
                      {peppolInvoice && (
                        <FunctionField
                          source={Ubl.price}
                          label='dxMessages.headers.priceDetails'
                          sortable={false}
                          render={(record) => {
                            const unitPrice = get(record, Ubl.price);
                            const currency = get(
                              record,
                              'price.priceAmount.currencyID'
                            );
                            const priceDiscount = get(
                              record,
                              'price.allowanceCharge[0]'
                            ); // cardinality [0..1], can direct access the first element
                            return (
                              <table>
                                <tbody>
                                  <tr>
                                    <td>
                                      <Typography
                                        variant='caption'
                                        style={{
                                          marginLeft: '0.5em',
                                        }}
                                      >
                                        {`${translate(
                                          'dxMessages.headers.unitNetPrice'
                                        )}  (${currency}) :`}
                                      </Typography>
                                    </td>
                                    <td>
                                      <Typography
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'flex-end',
                                        }}
                                        variant='caption'
                                      >
                                        {formatNumericValue(
                                          unitPrice,
                                          locale,
                                          2,
                                          2
                                        )}
                                      </Typography>
                                    </td>
                                  </tr>
                                  {priceDiscount && (
                                    <tr>
                                      <td>
                                        <Typography
                                          variant='caption'
                                          style={{
                                            marginLeft: '0.5em',
                                          }}
                                        >
                                          {`${translate(
                                            'dxMessages.headers.priceDiscountAmount'
                                          )}  (${currency}) :`}
                                        </Typography>
                                      </td>
                                      <td>
                                        <Typography
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                          }}
                                          variant='caption'
                                        >
                                          {formatNumericValue(
                                            priceDiscount.amount?.value,
                                            locale,
                                            2,
                                            2
                                          )}
                                        </Typography>
                                      </td>
                                    </tr>
                                  )}
                                  {priceDiscount && (
                                    <tr>
                                      <td>
                                        <Typography
                                          variant='caption'
                                          style={{
                                            marginLeft: '0.5em',
                                          }}
                                        >
                                          {`${translate(
                                            'dxMessages.headers.unitGrossPrice'
                                          )}  (${currency}) :`}
                                        </Typography>
                                      </td>
                                      <td>
                                        <Typography
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                          }}
                                          variant='caption'
                                        >
                                          {formatNumericValue(
                                            priceDiscount.baseAmount?.value,
                                            locale,
                                            2,
                                            2
                                          )}
                                        </Typography>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            );
                          }}
                          style={{ display: 'block', minWidth: '18em' }}
                        />
                      )}
                      {!peppolInvoice && (
                        <FunctionField
                          source={Ubl.discountMultiplierFactorNumeric}
                          label='dxMessages.headers.discount'
                          sortable={false}
                          render={(record) => {
                            const value = get(
                              record,
                              Ubl.discountMultiplierFactorNumeric
                            );
                            return (
                              <Typography>
                                {!isNaN(value) && value !== null
                                  ? `${value}%`
                                  : EmptyValue}
                              </Typography>
                            );
                          }}
                          style={{ minWidth: '3em' }}
                        />
                      )}
                      {peppolInvoice && (
                        <FunctionField
                          source={'discountDetails'}
                          label='dxMessages.headers.discountDetails'
                          sortable={false}
                          render={(record) => {
                            const discountDetails = get(
                              record,
                              'allowanceCharge'
                            )
                              ?.filter(
                                (e) => e.chargeIndicator?.value === false
                              )
                              ?.filter(
                                (e) =>
                                  e.amount?.value !== undefined &&
                                  e.amount?.value !== null
                              );
                            if (discountDetails?.length === 0) {
                              return null;
                            }
                            return (
                              <table>
                                <tbody>
                                  {discountDetails?.map((s) => {
                                    const percentage =
                                      s.multiplierFactorNumeric?.value;
                                    const amount = s.amount?.value;
                                    const currency = s.amount?.currencyID;
                                    const baseAmount = s.baseAmount?.value;
                                    const reason =
                                      s.allowanceChargeReason?.[0]?.value ||
                                      s.allowanceChargeReason?.value;
                                    return (
                                      <>
                                        <tr key={reason}>
                                          <td>
                                            <Typography
                                              style={{ fontStyle: 'italic' }}
                                            >
                                              {reason}
                                            </Typography>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>
                                            <Typography
                                              variant='caption'
                                              style={{
                                                marginLeft: '0.5em',
                                              }}
                                            >
                                              {`${translate(
                                                'dxMessages.headers.discount'
                                              )} :`}
                                            </Typography>
                                          </td>
                                          <td>
                                            <Typography
                                              style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                              }}
                                              variant='caption'
                                            >
                                              {formatNumericValue(
                                                percentage,
                                                locale,
                                                2,
                                                2
                                              )}
                                            </Typography>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>
                                            <Typography
                                              variant='caption'
                                              style={{
                                                marginLeft: '0.5em',
                                              }}
                                            >
                                              {`${translate(
                                                'dxMessages.headers.amount'
                                              )}  (${currency}) :`}
                                            </Typography>
                                          </td>
                                          <td>
                                            <Typography
                                              style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                              }}
                                              variant='caption'
                                            >
                                              {formatNumericValue(
                                                amount,
                                                locale,
                                                2,
                                                2
                                              )}
                                            </Typography>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>
                                            <Typography
                                              variant='caption'
                                              style={{
                                                marginLeft: '0.5em',
                                              }}
                                            >
                                              {`${translate(
                                                'dxMessages.headers.baseAmount'
                                              )}  (${currency}) :`}
                                            </Typography>
                                          </td>
                                          <td>
                                            <Typography
                                              style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                              }}
                                              variant='caption'
                                            >
                                              {formatNumericValue(
                                                baseAmount,
                                                locale,
                                                2,
                                                2
                                              )}
                                            </Typography>
                                          </td>
                                        </tr>
                                      </>
                                    );
                                  })}
                                </tbody>
                              </table>
                            );
                          }}
                          style={{ display: 'block', minWidth: '16em' }}
                        />
                      )}
                      {!peppolInvoice && (
                        <FunctionField
                          source={'taxType'}
                          label='dxMessages.headers.taxType'
                          sortable={false}
                          render={(record) => {
                            if (LineProcessor.isLineSGR(record as any)) {
                              return (
                                <Typography>
                                  {translate('dxMessages.headers.sgrTax')}
                                </Typography>
                              );
                            } else if (
                              DataHelpers.getGreenTaxValue(
                                DataHelpers.getGreenTaxNode(record as any)
                              )
                            ) {
                              return (
                                <Typography>
                                  {translate(
                                    'dxMessages.invoices.taxes.GT_Amount'
                                  )}
                                </Typography>
                              );
                            } else if (
                              DataHelpers.getSugarTaxValue(
                                DataHelpers.getSugarTaxNode(record as any)
                              )
                            ) {
                              return (
                                <Typography>
                                  {translate(
                                    'dxMessages.invoices.taxes.SGT_Amount'
                                  )}
                                </Typography>
                              );
                            } else {
                              return <Typography>{EmptyValue}</Typography>;
                            }
                          }}
                          style={{ minWidth: '3em' }}
                        />
                      )}
                      {!peppolInvoice && (
                        <FunctionField
                          source={'tax'}
                          label='dxMessages.invoices.taxes.label'
                          textAlign='right'
                          sortable={false}
                          render={(record) => {
                            if (LineProcessor.isLineSGR(record as any)) {
                              const value = get(
                                record,
                                'allowanceCharge[2].amount.value'
                              );
                              return (
                                <Typography>
                                  {value
                                    ? formatNumericValue(value, locale)
                                    : EmptyValue}
                                </Typography>
                              );
                            } else if (
                              DataHelpers.getGreenTaxValue(
                                DataHelpers.getGreenTaxNode(record as any)
                              )
                            ) {
                              const value = get(
                                record,
                                Ubl.greenTaxPerUnitAmount
                              );
                              return (
                                <Typography>
                                  {value
                                    ? formatNumericValue(value, locale)
                                    : EmptyValue}
                                </Typography>
                              );
                            } else if (
                              DataHelpers.getSugarTaxValue(
                                DataHelpers.getSugarTaxNode(record as any)
                              )
                            ) {
                              const value = get(
                                record,
                                Ubl.sugarTaxPerUnitAmount
                              );
                              return (
                                <Typography>
                                  {value
                                    ? formatNumericValue(value, locale)
                                    : EmptyValue}
                                </Typography>
                              );
                            } else {
                              return <Typography>{EmptyValue}</Typography>;
                            }
                          }}
                          style={{ minWidth: '3em' }}
                        />
                      )}
                      {peppolInvoice && (
                        <FunctionField
                          source={'chargeDetails'}
                          label='dxMessages.headers.chargeDetails'
                          sortable={false}
                          render={(record) => {
                            const chargeDetails = get(record, 'allowanceCharge')
                              ?.filter((e) => e.chargeIndicator?.value === true)
                              ?.filter(
                                (e) =>
                                  e.perUnitAmount?.value !== undefined &&
                                  e.perUnitAmount?.value !== null
                              );
                            if (chargeDetails?.length === 0) {
                              return null;
                            }
                            return (
                              <table>
                                <tbody>
                                  {chargeDetails?.map((s) => {
                                    const perUnitAmount =
                                      s.perUnitAmount?.value;
                                    const reason =
                                      s.allowanceChargeReason?.[0]?.value ||
                                      s.allowanceChargeReason?.value;
                                    const currency =
                                      s.perUnitAmount?.currencyID;
                                    return (
                                      <>
                                        <tr key={reason}>
                                          <td>
                                            <Typography
                                              style={{ fontStyle: 'italic' }}
                                            >
                                              {reason}
                                            </Typography>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td>
                                            <Typography
                                              variant='caption'
                                              style={{
                                                marginLeft: '0.5em',
                                              }}
                                            >
                                              {`${translate(
                                                'dxMessages.headers.perUnitAmount'
                                              )}  (${currency}) :`}
                                            </Typography>
                                          </td>
                                          <td>
                                            <Typography
                                              style={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                              }}
                                              variant='caption'
                                            >
                                              {formatNumericValue(
                                                perUnitAmount,
                                                locale,
                                                2,
                                                2
                                              )}
                                            </Typography>
                                          </td>
                                        </tr>
                                      </>
                                    );
                                  })}
                                </tbody>
                              </table>
                            );
                          }}
                          style={{ display: 'block', minWidth: '16em' }}
                        />
                      )}
                      <FunctionField
                        source={Ubl.vatPercentage}
                        label='dxMessages.headers.vat'
                        sortable={false}
                        render={(record) => {
                          const value = get(record, Ubl.vatPercentage);
                          return (
                            <Typography>
                              {!isNaN(value) && value !== null
                                ? `${value}%`
                                : EmptyValue}
                            </Typography>
                          );
                        }}
                        style={{ minWidth: '3em' }}
                      />
                      {(hasExemptionReasonCode ||
                        (peppolInvoice && hasNonStandardTaxCategory)) && (
                        <FunctionField
                          source={Ubl.taxSchemeName}
                          label='dxMessages.invoices.tax_category_code'
                          sortable={false}
                          render={(record) => {
                            // Priority is given to name over id.
                            // name can fit PEPPOL UBL.BE for instance.
                            // 1 : name in classifiedTaxCategory : most often non Draft UBL
                            // 2 : taxScheme name : most often Draft UBL
                            // 3 : id in classifiedTaxCategory : most often non Draft UBL.
                            let value = get(
                              record,
                              'item.classifiedTaxCategory[0].name.value'
                            );
                            if (value === undefined) {
                              value = get(record, Ubl.taxSchemeName);
                            }
                            if (value === undefined) {
                              get(
                                record,
                                'item.classifiedTaxCategory[0].id.value'
                              );
                            }
                            let { display: displayScheme, tooltip } =
                              getTaxCategoryDisplay(value, translate);
                            return tooltip ? (
                              <>
                                <Tooltip
                                  title={tooltip}
                                  style={{
                                    pointerEvents: 'initial',
                                    cursor: 'default',
                                  }}
                                >
                                  <Typography>
                                    {displayScheme !== null
                                      ? displayScheme
                                      : EmptyValue}
                                  </Typography>
                                </Tooltip>
                              </>
                            ) : (
                              <Typography>
                                {displayScheme !== null
                                  ? displayScheme
                                  : EmptyValue}
                              </Typography>
                            );
                          }}
                          style={{ minWidth: '3em' }}
                        />
                      )}
                      {hasExemptionReasonCode && (
                        <FunctionField
                          source={Ubl.taxExemptionReasonCode}
                          label='dxMessages.invoices.tax_reason_code'
                          sortable={false}
                          render={(record) => {
                            const value = get(
                              record,
                              Ubl.taxExemptionReasonCode
                            );
                            return (
                              <Typography>
                                {value ? value : EmptyValue}
                              </Typography>
                            );
                          }}
                        />
                      )}
                      {hasStartOrEndLinesPeriodDates && (
                        <FunctionField
                          source='invoicePeriod.startDate.value'
                          label='dxMessages.headers.invoicePeriodStartDate'
                          sortable={false}
                          render={(record) => {
                            const value =
                              get(record, 'invoicePeriod.startDate') ||
                              get(record, 'invoicePeriod[0].startDate');
                            return (
                              <GenericDateField
                                label=''
                                id='value'
                                record={value}
                                className={'lineInvoicePeriodStartDate'}
                                bold
                              />
                            );
                          }}
                        />
                      )}
                      {hasStartOrEndLinesPeriodDates && (
                        <FunctionField
                          source='invoicePeriod.endDate.value'
                          label='dxMessages.headers.invoicePeriodEndDate'
                          sortable={false}
                          render={(record) => {
                            const value =
                              get(record, 'invoicePeriod.endDate') ||
                              get(record, 'invoicePeriod[0].endDate');
                            return (
                              <GenericDateField
                                label=''
                                id='value'
                                record={value}
                                className={'lineInvoicePeriodEndDate'}
                                bold
                              />
                            );
                          }}
                        />
                      )}
                      {hasLineItemClassification && (
                        <FunctionField
                          source={Ubl.itemClassificationCode}
                          label='dxMessages.headers.itemClassificationCode'
                          sortable={false}
                          render={(record) => {
                            const value =
                              get(record, Ubl.itemClassificationCode) ||
                              get(
                                record,
                                'item.commodityClassification[0].itemClassificationCode.value'
                              );
                            return (
                              <Typography>
                                {value ? value : EmptyValue}
                              </Typography>
                            );
                          }}
                        />
                      )}
                      <PriceTextField
                        source={Ubl.totalWithoutVat}
                        label='dxMessages.headers.totalWithoutVat'
                        sortable={false}
                        headerClassName='alignRight'
                        cellClassName='alignRight'
                      />
                    </Datagrid>
                  </div>
                </ListContextProvider>
              </Grid>
              <Grid item container justify='flex-end'>
                <Grid item>
                  <TaxTotals data={data} />
                </Grid>
              </Grid>
              {taxSummaryTable.length > 0 && (
                <Grid item container justify='flex-end'>
                  <Grid item>
                    <TaxSummaryPanel data={data} />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
        {toolbar &&
          React.createElement(toolbar, {
            record: data,
            resource,
            basePath,
          })}
      </>
    );
  }
};

InvoicePreview.propTypes = {
  toolbar: ComponentPropType,
  record: PropTypes.object.isRequired,
  basePath: PropTypes.string,
  resource: PropTypes.string,
};

export default InvoicePreview;
